/**
 * Authentication service for handling login, logout, and token management
 */
import API_CONFIG from '../config';
import axios from 'axios';

const API_URL = API_CONFIG.AUTH_URL;

/**
 * Login user with email and password
 * @param {string} email User email
 * @param {string} password User password
 * @returns {Promise} Promise with user data and token
 */
export const login = async (email, password) => {
  try {
    const response = await axios.post('/auth/login', 
      { email, password },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );

    const data = response.data;
    
    // Store auth data in localStorage
    if (data.accessToken) {
      storeUserData(data);
    }
    
    return data;
  } catch (error) {
    console.error('Login error details:', error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const message = error.response.data?.message || 'Login failed';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Invalid credentials');
        case 403:
          throw new Error('Access forbidden. Please check your permissions.');
        case 404:
          throw new Error('Login service not found. Please contact support.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Login failed (${status})`);
      }
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      // Something happened in setting up the request
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Logout the current user
 * @param {function} navigate - React Router's navigate function (optional)
 */
export const logout = (navigate) => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('userCompanyId');
  
  if (navigate) {
    navigate('/');
  } else {
    window.location.href = '/';
  }
};

/**
 * Store user authentication data in localStorage
 * @param {Object} data Authentication response data 
 */
export const storeUserData = (data) => {
  localStorage.setItem('accessToken', data.accessToken);
  localStorage.setItem('userRole', data.user.role);
  localStorage.setItem('userName', data.user.name);
  localStorage.setItem('userEmail', data.user.email);
  localStorage.setItem('userId', data.user.id.toString());
  localStorage.setItem('userCompanyId', data.user.company?.id?.toString() || '');
};

/**
 * Get the current user's authentication token
 * @returns {string|null} Auth token or null if not authenticated
 */
export const getToken = () => {
  return localStorage.getItem('accessToken');
};

/**
 * Check if the current token is valid and not expired
 * @returns {boolean} Whether the token is valid
 */
export const isTokenValid = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;

  try {
    // Decode the JWT token
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const { exp } = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if token is expired
    if (exp < currentTime) {
      // Clear invalid token
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
      localStorage.removeItem('userCompanyId');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Check if user is authenticated with valid token
 * @returns {boolean} Whether user is authenticated
 */
export const isAuthenticated = () => {
  return isTokenValid();
};

/**
 * Get current user role
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  return localStorage.getItem('userRole');
};

/**
 * Check if current user is admin
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  return getUserRole() === 'ADMIN';
};

/**
 * Get current user data from localStorage
 * @returns {Object} User data
 */
export const getCurrentUser = () => {
  return {
    id: localStorage.getItem('userId'),
    name: localStorage.getItem('userName'),
    email: localStorage.getItem('userEmail'),
    role: localStorage.getItem('userRole'),
    companyId: localStorage.getItem('userCompanyId')
  };
};

/**
 * Get the authorization header needed for API requests
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeader = () => {
  const token = getToken();
  return token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  } : {};
};

// Assign to a variable before exporting
const authService = {
  login,
  logout,
  getToken,
  isAuthenticated,
  getUserRole,
  isAdmin,
  getCurrentUser,
  getAuthHeader
};

export default authService; 